"use client";

import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Modal,
  CircularProgress,
  IconButton,
  Pagination,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  useTheme,
  Paper,
  TablePagination,
  Divider,
  Chip
} from "@mui/material";
import {
  Search,
  Filter,
  Download,
  Eye,
  FileText,
  CreditCard,
  PieChart,
  BarChart,
  X
} from "lucide-react";
import { useColorMode } from "../../../theme/ThemeContext";
import Chart from "../admincomponents/Chart";

const BillingPayments = () => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDateRange, setFilterDateRange] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Sample payment data (preserved from original)
  const [paymentsData] = useState([
    {
      id: "INV-001",
      user: "John Doe",
      email: "john@example.com",
      amount: 49.99,
      date: "2023-03-15",
      status: "Paid",
      method: "Credit Card",
      items: [{ name: "Introduction to React", price: 49.99 }],
    },
    {
      id: "INV-002",
      user: "Jane Smith",
      email: "jane@example.com",
      amount: 69.99,
      date: "2023-03-10",
      status: "Paid",
      method: "PayPal",
      items: [{ name: "Advanced Python Programming", price: 69.99 }],
    },
    {
      id: "INV-003",
      user: "Bob Johnson",
      email: "bob@example.com",
      amount: 39.99,
      date: "2023-03-05",
      status: "Pending",
      method: "Bank Transfer",
      items: [{ name: "Digital Marketing Fundamentals", price: 39.99 }],
    },
    {
      id: "INV-004",
      user: "Alice Brown",
      email: "alice@example.com",
      amount: 79.99,
      date: "2023-02-28",
      status: "Paid",
      method: "Credit Card",
      items: [{ name: "Data Science with Python", price: 79.99 }],
    },
    {
      id: "INV-005",
      user: "Charlie Wilson",
      email: "charlie@example.com",
      amount: 59.99,
      date: "2023-02-25",
      status: "Failed",
      method: "Credit Card",
      items: [{ name: "UX/UI Design Principles", price: 59.99 }],
    },
    {
      id: "INV-006",
      user: "Diana Miller",
      email: "diana@example.com",
      amount: 149.99,
      date: "2023-02-20",
      status: "Paid",
      method: "PayPal",
      items: [{ name: "Full Stack Web Development", price: 149.99 }],
    },
  ]);

  // Filter logic (preserved)
  const filteredPayments = paymentsData.filter((payment) => {
    const matchesSearch =
      payment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "" || payment.status === filterStatus;
    const matchesDateRange = true;
    return matchesSearch && matchesStatus && matchesDateRange;
  });

  const handleChangePage = (event, newPage) => setCurrentPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const currentRows = filteredPayments.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  // Chart data (preserved)
  const revenueChartData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Revenue",
        data: [12500, 19000, 15000, 22000, 18000, 24000],
        borderColor: "#FFD700",
        backgroundColor: "rgba(255, 215, 0, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const paymentMethodsChartData = {
    labels: ["Credit Card", "PayPal", "Bank Transfer", "Other"],
    datasets: [
      {
        data: [65, 25, 8, 2],
        backgroundColor: [
          "#B8860B",
          "#DAA520",
          "#FFD700",
          "#FFFACD",
        ],
        borderWidth: 0,
      },
    ],
  };

  const handleViewInvoice = (invoice) => {
    setCurrentInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', md: 700 },
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  return (
    <Box sx={{ p: 4, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 800, color: 'text.primary', textAlign: 'center' }}>
        Billing & Payments
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BarChart size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" sx={{ ml: 1, fontWeight: 700 }}>Revenue Overview</Typography>
            </Box>
            <Chart type="line" data={revenueChartData} height="300px" />
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PieChart size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" sx={{ ml: 1, fontWeight: 700 }}>Payment Methods</Typography>
            </Box>
            <Chart type="doughnut" data={paymentMethodsChartData} height="300px" />
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ p: 0, bgcolor: 'background.paper', overflow: 'hidden' }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Payment History</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
              size="small"
              sx={{ width: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(0); }}
                  startAdornment={<Filter size={14} style={{ marginRight: 8 }} />}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<Download size={18} />}
                sx={{
                  background: 'linear-gradient(135deg, #CFB53B 0%, #B8860B 100%)',
                  '&:hover': { background: theme.palette.primary.dark }
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ color: 'primary.main', fontWeight: 700 }}>Invoice ID</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 700 }}>Method</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{row.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.user}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>${row.amount.toFixed(2)}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.method}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: row.status === "Paid" ? "#dcfce7" : row.status === "Pending" ? "#fef3c7" : "#fee2e2",
                        color: row.status === "Paid" ? "#166534" : row.status === "Pending" ? "#92400e" : "#991b1b",
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleViewInvoice(row)} sx={{ color: 'primary.main', mr: 1 }}>
                      <Eye size={18} />
                    </IconButton>
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      <Download size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={filteredPayments.length}
          page={currentPage}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Card>

      <Modal open={showInvoiceModal} onClose={() => setShowInvoiceModal(false)}>
        <Box sx={modalStyle}>
          {currentInvoice && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>Invoice</Typography>
                  <Typography variant="subtitle2" color="text.secondary">#{currentInvoice.id}</Typography>
                </Box>
                <FileText size={40} color={theme.palette.primary.main} />
              </Box>

              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>Billed To</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>{currentInvoice.user}</Typography>
                  <Typography variant="body2" color="text.secondary">{currentInvoice.email}</Typography>
                </Grid>
                <Grid item xs={6} align="right">
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>Details</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}><strong>Date:</strong> {currentInvoice.date}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={currentInvoice.status}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: currentInvoice.status === "Paid" ? "#dcfce7" : "#fee2e2",
                        color: currentInvoice.status === "Paid" ? "#166534" : "#991b1b",
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentInvoice.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">${item.price.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ borderBottom: 'none', pt: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Total Amount</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: 'none', pt: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        ${currentInvoice.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Box sx={{ mt: 4, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Payment Method</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <CreditCard size={16} style={{ marginRight: 8 }} />
                  <Typography variant="body2">{currentInvoice.method}</Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={() => setShowInvoiceModal(false)}>Close</Button>
                <Button variant="contained" startIcon={<Download size={18} />}>Download PDF</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default BillingPayments;
